import * as React from 'react'
import { useDeclareFocus } from '@/contexts/where-i-am'
import { Label } from '@/components/ui/label'
import {
  Bot,
  Clock3,
  Copy,
  Edit,
  Plus,
  Power,
  PowerOff,
  Search,
  Trash2,
  Wrench,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AgentAvatar } from '@/components/agents/AgentVisualEditor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AgentForm } from '@/components/agents/AgentForm'
import { loadAgentVisualsMap } from '@/lib/agent-visuals'
import { agentsService } from '@/services/agentsService'
import { cn } from '@/lib/utils'
import { log } from '@/lib/logger'
import type { Agent } from '@/types/agent'
import { useIsMobile } from '@/hooks/use-mobile'
import { PageHeader } from '@/components/ui/page-header'

function normalizeId(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function formatRelativeTime(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  const ts = date.getTime()
  if (!Number.isFinite(ts)) return '-'
  const diffMs = Date.now() - ts
  const diffHours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)))
  if (diffHours < 24) return `hace ${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `hace ${diffDays}d`
  const diffMonths = Math.floor(diffDays / 30)
  return `hace ${diffMonths}m`
}

export function AgentsPage() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const isMobile = useIsMobile()

  const [agents, setAgents] = React.useState<Agent[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [formOpen, setFormOpen] = React.useState(false)
  const [selectedAgent, setSelectedAgent] = React.useState<Agent | null>(null)
  // **Lo que el usuario tiene delante, dicho al Brain** (3.5, D2). Sin esto,
  // «duplica este agente» le obliga a preguntar cual, que es la pregunta que
  // el usuario acaba de contestar con el raton. Se retira solo al salir de
  // esta pantalla.
  useDeclareFocus('agent', selectedAgent?.id)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [agentToDelete, setAgentToDelete] = React.useState<Agent | null>(null)
  const [duplicatingId, setDuplicatingId] = React.useState<string | null>(null)
  const [agentVisuals, setAgentVisuals] = React.useState(loadAgentVisualsMap)
  const selectedAgentIdRef = React.useRef<string | null>(null)

  const [searchQuery, setSearchQuery] = React.useState('')
  const [enabledFilter, setEnabledFilter] = React.useState<string>('all')

  const langPrefix = `/${lang || 'en'}`

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const enabledParam =
        enabledFilter === 'all' ? undefined : enabledFilter === 'enabled'
      const data = await agentsService.listAgents({ enabled: enabledParam })
      setAgents(data.filter((agent) => agent.name !== 'master-graph'))
    } catch (loadError) {
      log.error('AgentsPage loadData failed', loadError, { component: 'AgentsPage' })
      setError(toErrorMessage(loadError))
    } finally {
      setLoading(false)
    }
  }, [enabledFilter])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    selectedAgentIdRef.current = selectedAgent?.id ?? null
  }, [selectedAgent?.id])

  const filteredAgents = React.useMemo(() => {
    if (!searchQuery.trim()) return agents
    const query = searchQuery.toLowerCase()
    return agents.filter((agent) => {
      return (
        agent.id.toLowerCase().includes(query) ||
        agent.name.toLowerCase().includes(query) ||
        agent.display_name.toLowerCase().includes(query) ||
        agent.description?.toLowerCase().includes(query)
      )
    })
  }, [agents, searchQuery])
  const loadingCards = React.useMemo(() => Array.from({ length: 6 }), [])

  const handleCreate = () => {
    navigate(`${langPrefix}/agent-studio`)
  }

  const handleEdit = async (agentId: string) => {
    try {
      setSelectedAgent(null)
      const detail = await agentsService.getAgent(agentId)
      setSelectedAgent(detail)
      setFormOpen(true)
    } catch (editError) {
      log.error('AgentsPage handleEdit failed', editError, { agentId })
      setError(toErrorMessage(editError))
    }
  }

  const handleDelete = (agent: Agent) => {
    setAgentToDelete(agent)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!agentToDelete) return
    try {
      await agentsService.deleteAgent(agentToDelete.id)
      setDeleteDialogOpen(false)
      setAgentToDelete(null)
      await loadData()
    } catch (deleteError) {
      log.error('AgentsPage confirmDelete failed', deleteError, { agentId: agentToDelete.id })
      setError(toErrorMessage(deleteError))
    }
  }

  const handleToggleEnabled = async (agent: Agent) => {
    try {
      if (agent.is_enabled) {
        await agentsService.disableAgent(agent.id)
      } else {
        await agentsService.enableAgent(agent.id)
      }
      await loadData()
    } catch (toggleError) {
      log.error('AgentsPage handleToggleEnabled failed', toggleError, { agentId: agent.id })
      setError(toErrorMessage(toggleError))
    }
  }

  const handleDuplicate = async (agent: Agent) => {
    try {
      setDuplicatingId(agent.id)
      const detail = await agentsService.getAgent(agent.id)
      const suffix = Date.now().toString().slice(-5)
      const basePrompt = detail.base_prompt ?? detail.custom_instructions ?? ''
      const payload = {
        name: `${detail.name}-copy-${suffix}`,
        display_name: `${detail.display_name} (copia)`,
        description: detail.description,
        is_enabled: false,
        prompt_mode: detail.prompt_mode ?? 'isolated',
        base_prompt: basePrompt,
        custom_instructions: detail.custom_instructions,
        tool_ids: detail.tool_ids ?? detail.tools?.map((tool) => tool.id),
        audience: detail.audience ?? 'internal',
      }
      await agentsService.createAgent(payload)
      await loadData()
    } catch (duplicateError) {
      log.error('AgentsPage handleDuplicate failed', duplicateError, { agentId: agent.id })
      setError(toErrorMessage(duplicateError))
    } finally {
      setDuplicatingId(null)
    }
  }

  const handleFormSuccess = () => {
    setAgentVisuals(loadAgentVisualsMap())
    const agentId = selectedAgentIdRef.current
    void (async () => {
      await loadData()
      if (!agentId) return
      try {
        const fresh = await agentsService.getAgent(agentId)
        setSelectedAgent(fresh)
      } catch (refreshError) {
        log.error('AgentsPage refetch agent after save failed', refreshError, {
          component: 'AgentsPage',
          agentId,
        })
      }
    })()
  }

  const handleFormOpenChange = React.useCallback((open: boolean) => {
    setFormOpen(open)
    if (!open) {
      setSelectedAgent(null)
    }
  }, [])

  return (
    <div className="container mx-auto space-y-4 p-3 md:p-6">
      {isMobile ? (
        <section className="space-y-4">
          <div className="space-y-2">
            <PageHeader title="Agentes" />
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="agents-search"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="h-10 pl-9"
                />
              </div>
              <Select value={enabledFilter} onValueChange={setEnabledFilter}>
                <SelectTrigger id="agents-status" className="h-10 w-[110px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="enabled">On</SelectItem>
                  <SelectItem value="disabled">Off</SelectItem>
                </SelectContent>
              </Select>
              <Button size="icon" className="shrink-0" onClick={handleCreate} aria-label="Crear agente">
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section>
            {/* Animacion codex deshabilitada por solicitud del usuario */}
            {/* <div className="codex-hero-bg" aria-hidden /> */}
            {/* <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0d1732]/28 via-transparent to-[#0d1732]/28" /> */}

            <PageHeader title="Agentes">
              <Button size="lg" onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Crear agente
              </Button>
            </PageHeader>
          </section>
          <section>
            <div className="flex flex-col gap-2 md:flex-row md:items-end">
              <div className="flex-1">
                <Label htmlFor="agents-search" className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                  Buscar agente
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="agents-search"
                    placeholder="Nombre visible, interno o descripción"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="h-11 pl-9"
                  />
                </div>
              </div>

              <div className="w-full md:w-64">
                <Label htmlFor="agents-status" className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
                  Estado
                </Label>
                <Select value={enabledFilter} onValueChange={setEnabledFilter}>
                  <SelectTrigger id="agents-status" className="h-11">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="enabled">Habilitados</SelectItem>
                    <SelectItem value="disabled">Deshabilitados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        </>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <section className="space-y-2">
          {loadingCards.map((_, index) => (
            <article key={`agent-skeleton-${index}`} className="rounded-lg border bg-card/80 p-4">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex flex-1 items-start gap-2">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Skeleton className="h-6 w-44" />
                      <Skeleton className="h-5 w-24 rounded-full" />
                      <Skeleton className="h-4 w-28" />
                    </div>
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <Skeleton className="h-9 w-9 rounded-md" />
                </div>
              </div>
            </article>
          ))}
        </section>
      ) : filteredAgents.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bot className="h-6 w-6" />
          </div>
          <h2 className="mt-3 text-xl font-semibold">No hay agentes para mostrar</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {searchQuery || enabledFilter !== 'all'
              ? 'Prueba con otros filtros o limpia la búsqueda.'
              : 'Crea tu primer agente con el flujo guiado.'}
          </p>
          <Button className="mt-4" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Crear agente
          </Button>
        </div>
      ) : (
        <section className="space-y-2">
          {filteredAgents.map((agent) => {
            const toolsCount = agent.tool_count ?? agent.tools?.length ?? 0
            const updatedAt = formatRelativeTime(agent.updated_at)
            const createdAt = formatRelativeTime(agent.created_at)
            const visual = agentVisuals[normalizeId(agent.id)]
            const canDisable = agent.can_disable ?? true
            const canEdit = agent.can_edit ?? true
            const canDelete = agent.can_delete ?? true

            return (
              <article
                key={agent.id}
                className={cn(
                  'rounded-lg border bg-card/80 p-4 transition-colors',
                  'hover:border-primary/40 hover:bg-card'
                )}
              >
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex flex-1 items-start gap-2">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-muted/40">
                      <AgentAvatar visual={visual} fallbackClassName="bg-muted/30" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                      <h2 className="max-w-full truncate text-lg font-semibold" title={agent.display_name}>
                        {agent.display_name}
                      </h2>
                      <Badge variant={agent.is_enabled ? 'default' : 'secondary'}>
                        {agent.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                      </Badge>
                      {agent.is_system && (
                        <Badge variant="outline">Interno</Badge>
                      )}
                      <span className="min-w-0 max-w-full truncate font-mono text-xs text-primary sm:max-w-[260px]" title={agent.name}>
                        {agent.name}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-1 break-all text-sm text-muted-foreground" title={agent.description || 'Sin descripción.'}>
                      {agent.description || 'Sin descripción.'}
                    </p>
                    <p className="mt-1 max-w-[520px] truncate font-mono text-[11px] text-muted-foreground" title={agent.id}>
                      ID: {agent.id}
                    </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Wrench className="h-3.5 w-3.5 text-primary" />
                      {toolsCount} herramientas
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock3 className="h-3.5 w-3.5 text-primary" />
                      Actualizado {updatedAt}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock3 className="h-3.5 w-3.5 text-primary" />
                      Creado {createdAt}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleEnabled(agent)}
                      title={agent.is_enabled ? 'Deshabilitar' : 'Habilitar'}
                      disabled={!canDisable}
                    >
                      {agent.is_enabled ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(agent.id)}
                      title="Editar"
                      disabled={!canEdit}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(agent)}
                      title="Duplicar"
                      disabled={duplicatingId === agent.id || !canEdit}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(agent)}
                      title="Eliminar"
                      disabled={!canDelete}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </article>
            )
          })}
        </section>
      )}

      {selectedAgent && (
        <AgentForm
          key={`${selectedAgent.id}:${selectedAgent.updated_at ?? ''}`}
          open={formOpen}
          onOpenChange={handleFormOpenChange}
          agent={selectedAgent}
          onSuccess={handleFormSuccess}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar agente</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el agente <strong>{agentToDelete?.display_name}</strong> y no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
